import React, { useEffect, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import { fetchCrops, fetchSymptomsByLevel } from "../api/cropService";
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '../api/languageHelper';
import { getaccesstoken } from '../context/AuthContext';

const apiPrefix = process.env.REACT_APP_API_PREFIX;

const EditHierarchy = () => {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [selectedCrop, setSelectedCrop] = useState(null);
  const [l2Symptoms, setL2Symptoms] = useState([]);
  const [l3Symptoms, setL3Symptoms] = useState([]);
  const [updatedParents, setUpdatedParents] = useState({});
  const [loading, setLoading] = useState(false);

  // Load crops initially
  useEffect(() => {
    const loadCrops = async () => {
      try {
        const data = await fetchCrops(true);
        setCrops(data);
        if (data.length > 0) setSelectedCrop(data[0]);
      } catch (error) {
        console.error(t('expert.editHierarchy.errorLoadingCrops'), error);
      }
    };
    loadCrops();
  }, [t]);

  // Load hierarchy when crop changes
  useEffect(() => {
    const loadSymptoms = async () => {
      if (!selectedCrop) return;
      setLoading(true);
      try {
        const allL2 = await fetchSymptomsByLevel(selectedCrop.cid, 2, true);
        setL2Symptoms(allL2);

        const allL3 = await fetchSymptomsByLevel(selectedCrop.cid, 3, true);
        setL3Symptoms(allL3);

        const initialMapping = {};
        allL3.forEach((s) => {
          initialMapping[s.sid] = s.parentId;
        });
        setUpdatedParents(initialMapping);
      } catch (error) {
        console.error(t('expert.editHierarchy.errorLoadingHierarchy'), error);
      } finally {
        setLoading(false);
      }
    };
    loadSymptoms();
  }, [selectedCrop, t]);

  // Handle change of parent dropdown
  const handleParentChange = (sid, newPid) => {
    setUpdatedParents((prev) => ({ ...prev, [sid]: parseInt(newPid, 10) }));
  };

  // Submit updates
  const handleSubmit = async () => {
    try {
      const locale = getCurrentLanguage();
      const headers = { 'auth-token': getaccesstoken() }; // axiosInstance interceptor sets accept-language
      for (const sid of Object.keys(updatedParents)) {
        const parent_id = updatedParents[sid];
        const symptom = l3Symptoms.find((s) => s.sid === parseInt(sid));
        if (!symptom) continue;
        const payload = {
          parent_id: parent_id, 
          grandparent_id: 0,
          level: 3,
          locale_code: locale,
          name: symptom.name,
          description: symptom.description || '', 
          question: symptom.qn || ''
        };
        await axiosInstance.put(`${apiPrefix}symptom/${selectedCrop.cid}/${sid}`, payload, { headers });
      }
      alert(t('expert.editHierarchy.successMessage'));
    } catch (error) {
      console.error(t('expert.editHierarchy.errorUpdatingHierarchy'), error);
      alert(t('expert.editHierarchy.failMessage'));
    }
  };

  return (
    <div className="flex h-screen">
      {/* LEFT: Edit Hierarchy */}
      <div className="w-2/3 p-6 overflow-y-auto bg-gray-50">
        {loading ? (
          <p>{t('expert.editHierarchy.loading')}</p>
        ) : (
          <>
            {selectedCrop && (
              <h2 className="text-2xl font-bold mb-4">
                {t('expert.editHierarchy.pageTitle', { cropName: selectedCrop.name })}
              </h2>
            )}
            <div className="space-y-4">
              {l3Symptoms.map((symptom) => (
                <div
                  key={symptom.sid}
                  className="flex items-center space-x-4 p-3 bg-white rounded shadow"
                >
                  <div className="flex-1">{symptom.name}</div>
                  <select
                    value={updatedParents[symptom.sid] || ""}
                    onChange={(e) =>
                      handleParentChange(symptom.sid, e.target.value)
                    }
                    className="border rounded p-2 max-w-[180px] truncate"
                  >
                    <option
                      value=""
                      className="truncate"
                      title={t('expert.editHierarchy.selectPlaceholder')}
                    >
                      {t('expert.editHierarchy.selectPlaceholder')}
                    </option>
                    {l2Symptoms.map((parent) => (
                      <option
                        key={parent.sid}
                        value={parent.sid}
                        className="truncate"
                        title={parent.name}
                      >
                        {parent.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            {l3Symptoms.length > 0 && (
              <button
                onClick={handleSubmit}
                className="mt-6 bg-blue-600 text-white px-6 py-2 rounded shadow hover:bg-blue-700"
              >
                {t('expert.editHierarchy.submitButton')}
              </button>
            )}
          </>
        )}
      </div>

      {/* RIGHT: Crop List */}
      <div className="w-1/3 p-6 border-l border-gray-200 overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">{t('expert.editHierarchy.existingCropsTitle')}</h2>
        <ul>
          {crops.map((crop) => (
            <li
              key={crop.cid}
              onClick={() => setSelectedCrop(crop)}
              className={`cursor-pointer p-3 rounded mb-2 transition ${selectedCrop?.cid === crop.cid
                ? "bg-blue-600 text-white"
                : "hover:bg-gray-100"
                }`}
            >
              {crop.name}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default EditHierarchy;
